<script setup lang="ts">
import privacyPolicy from '~/data/wikibitPrivacyPolicy.json'
import servicePolicy from '~/data/wikibitServicePolicy.json'

type PolicyLocale = 'zh-cn' | 'zh' | 'en'
type PolicyTab = 'service' | 'privacy'

const props = defineProps<{
  tab: PolicyTab
}>()

const { t, locale, localePath } = useAppI18n()
const route = useRoute()
const appStore = useAppStore()
const termsPageTitle = await getSeoLocaleMessage(locale, '84139')

useHead(() => ({
  title: termsPageTitle,
  meta: [
    {
      name: 'description',
      content: t('84157'),
    },
  ],
}))

const policyMap = {
  service: servicePolicy,
  privacy: privacyPolicy,
}

const policyTabs = ['service', 'privacy'] as const

const tabLabels: Record<PolicyLocale, Record<PolicyTab, string>> = {
  'zh-cn': {
    service: '服务政策',
    privacy: '隐私政策',
  },
  zh: {
    service: '服務政策',
    privacy: '隱私政策',
  },
  en: {
    service: 'Service Policy',
    privacy: 'Privacy Policy',
  },
}

// 根据站点语言选择对应的政策正文版本。
const resolvePolicyLocale = (languageCode: string): PolicyLocale => {
  const normalizedLanguageCode = languageCode.toLowerCase()

  if (normalizedLanguageCode === 'zh-cn') {
    return 'zh-cn'
  }

  if (normalizedLanguageCode === 'zh' || normalizedLanguageCode === 'zh-hk' || normalizedLanguageCode === 'zh-tw') {
    return 'zh'
  }

  return 'en'
}

const activeTab = computed<PolicyTab>(() => props.tab)
const routeMatchedTab = computed<PolicyTab | null>(() => {
  const normalizedPath = route.path.toLowerCase()

  if (normalizedPath.includes('/terms/privacy')) {
    return 'privacy'
  }

  if (normalizedPath.includes('/terms/service')) {
    return 'service'
  }

  return null
})

const selectedTab = computed<PolicyTab>(() => routeMatchedTab.value || activeTab.value)

const policyLocale = computed(() => {
  return resolvePolicyLocale(appStore.LanguageCode)
})

const activePolicy = computed(() => {
  return policyMap[selectedTab.value][policyLocale.value]
})

const currentTabLabels = computed(() => tabLabels[policyLocale.value])

// 切换页签时直接跳转到对应的规范路径，保证 URL 可被搜索引擎收录。
const switchPolicyTab = async (tab: PolicyTab) => {
  if (tab === selectedTab.value) {
    return
  }

  await navigateTo(localePath(`/terms/${tab}`))
}
</script>

<template>
  <main class="terms-page" dir="ltr">
    <section class="terms-tabs" aria-label="Terms policy tabs" data-node-id="6513:53651">
      <button
        v-for="tab in policyTabs"
        :key="tab"
        type="button"
        class="terms-tab cursor-pointer hover:opacity-80"
        :class="selectedTab === tab ? 'terms-tab--active' : 'terms-tab--inactive'"
        :aria-pressed="selectedTab === tab"
        @click="switchPolicyTab(tab)"
      >
        <span class="terms-tab-text" :class="{ 'terms-tab-text--inactive': selectedTab !== tab }">
          {{ currentTabLabels[tab] }}
        </span>
      </button>
    </section>

    <section class="terms-content-card" aria-labelledby="termsPolicyTitle" data-node-id="6513:53660">
      <h1 id="termsPolicyTitle" class="terms-title" data-node-id="6513:53661">{{ activePolicy.title }}</h1>
      <div class="terms-body" data-node-id="6513:53662" v-html="activePolicy.content" />
    </section>
  </main>
</template>

<style lang="less" scoped>
.terms-page {
  direction: ltr;
  overflow-x: clip;
  padding-bottom: 194px;
  background: #fafafa;
}

.terms-tabs {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  height: 74px;
  padding: 0 360px;
  margin-top: 70px;
  font-family: 'PingFang SC', 'PingFang_SC', sans-serif;
  background: #fff;
  gap: 66px;
  filter: drop-shadow(0 3px 3px rgb(0 0 0 / 5%));
}

.terms-tab {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  height: 68px;
  padding: 20px 0;
  font: inherit;
  background: #fff;
  border: 0;
  transform: translateY(3px);
  transition: opacity 0.2s ease;

  &--active {
    box-shadow: inset 0 -1px 0 0 #3d3d3d;
  }

  &--inactive {
    min-width: 112px;
  }
}

.terms-tab-text {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  line-height: normal;
  color: #3d3d3d;
  text-align: center;
  white-space: nowrap;

  &--inactive {
    font-size: 20px;
    font-weight: 400;
    color: #a3a3a3;
  }
}

.terms-content-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 1200px;
  box-sizing: border-box;
  padding: 62px 60px;
  margin: 50px auto 0;
  background: #fff;
  border-radius: 4px;
  gap: 48px;
}

.terms-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: normal;
  color: #292a2e;
  text-align: center;
  white-space: nowrap;
}

.terms-body {
  min-width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  color: #3d3d3d;
  text-align: justify;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgb(61 61 61 / 24%) transparent;

  :deep(p) {
    margin: 0;
    white-space: pre-wrap;
  }

  :deep(a) {
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: #0d3f63;
    }
  }

  :deep(ul) {
    margin: 0;
    padding-left: 24px;
  }

  :deep(table) {
    width: 100%;
    min-width: 720px;
    margin: 20px 0;
    border: 1px solid #d9dde3;
    border-radius: 12px;
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 8px 24px rgb(17 24 39 / 6%);
  }

  :deep(table thead th) {
    padding: 14px 16px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: #1f2937;
    text-align: left;
    vertical-align: middle;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
    border-right: 1px solid #d9dde3;
    border-bottom: 1px solid #d9dde3;
  }

  :deep(table thead th:last-child) {
    border-right: 0;
  }

  :deep(table tbody td) {
    padding: 14px 16px;
    font-size: 15px;
    line-height: 1.7;
    color: #3d3d3d;
    vertical-align: top;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    word-break: break-word;
  }

  :deep(table tbody td:last-child) {
    border-right: 0;
  }

  :deep(table tbody tr:last-child td) {
    border-bottom: 0;
  }

  :deep(table tbody tr:nth-child(even)) {
    background: #fafbfc;
  }

  :deep(table tbody tr:hover) {
    background: #f5f8fb;
  }

  :deep(table p) {
    margin: 0;
  }

  :deep(table a) {
    word-break: break-all;
  }
}

@media screen and (max-width: 1500px) {
  .terms-tabs {
    padding: 0 120px;
  }
}

@media screen and (max-width: 1280px) {
  .terms-page {
    padding-bottom: 120px;
  }

  .terms-tabs {
    padding: 0 40px;
  }

  .terms-content-card {
    width: calc(100% - 80px);
    padding: 40px 32px;
    margin-top: 32px;
  }
}

@media screen and (max-width: 768px) {
  .terms-page {
    padding-bottom: 80px;
  }

  .terms-tabs {
    height: auto;
    padding: 0 16px;
    margin-top: 20px;
    gap: 20px;
  }

  .terms-tab {
    min-width: auto;
    height: 52px;
    padding: 12px 0;

    &--inactive {
      min-width: auto;
    }
  }

  .terms-tab-text {
    font-size: 18px;

    &--inactive {
      font-size: 16px;
    }
  }

  .terms-content-card {
    width: calc(100% - 32px);
    padding: 24px 16px;
    margin-top: 20px;
    gap: 24px;
  }

  .terms-title {
    white-space: normal;
  }

  .terms-body {
    font-size: 14px;
  }

  .terms-body :deep(table) {
    min-width: 600px;
    margin: 16px 0;
    border-radius: 10px;
  }

  .terms-body :deep(table thead th),
  .terms-body :deep(table tbody td) {
    padding: 12px 14px;
    font-size: 13px;
  }
}
</style>
